import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { LogIn, LogOut, Bookmark, Loader2, Sparkles, Settings } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useEntitlement } from "@/context/EntitlementContext";
import { postPortal } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import PricingModal from "@/components/PricingModal";

export default function AuthMenu({ returnPath = "/" }) {
  const { user, loading, logout, showSignIn } = useAuth();
  const { entitlement } = useEntitlement();
  const [open, setOpen] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { portal_url } = await postPortal(window.location.origin + returnPath);
      window.location.href = portal_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open Stripe portal");
      setPortalLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest px-3 py-2 text-ink-muted" data-testid="auth-loading">
        <Loader2 size={14} className="animate-spin" />
      </div>
    );
  }

  const isPremium = entitlement?.is_premium;

  if (!user) {
    return (
      <div className="inline-flex items-center gap-2">
        <button
          onClick={() => { trackEvent("upgrade_click", { source: "authmenu" }); setShowPricing(true); }}
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest border-2 border-press-red bg-press-red text-paper px-3 py-2 shadow-hard-sm hover:shadow-hard-hover"
          style={{ transition: "transform 150ms ease, box-shadow 150ms ease" }}
          data-testid="upgrade-btn"
        >
          <Sparkles size={14} /> Go Premium
        </button>
        <button
          data-testid="google-login-btn"
          onClick={() => { trackEvent("auth_login_start"); showSignIn(returnPath); }}
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest border-2 border-ink px-3 py-2 bg-paper-surface shadow-hard-sm hover:shadow-hard-hover"
          style={{ transition: "transform 150ms ease, box-shadow 150ms ease" }}
        >
          <LogIn size={14} /> Sign in
        </button>
        <PricingModal open={showPricing} onClose={() => setShowPricing(false)} reason="manual" playsRemaining={entitlement?.plays_remaining ?? 2} />
      </div>
    );
  }

  const initials = (user.name || user.email || "?")
    .split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

  return (
    <div className="relative inline-flex items-center gap-2" ref={menuRef} data-testid="auth-menu">
      {!isPremium && (
        <button
          onClick={() => { trackEvent("upgrade_click", { source: "authmenu" }); setShowPricing(true); }}
          className="hidden sm:inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest border-2 border-press-red bg-press-red text-paper px-3 py-2 shadow-hard-sm hover:shadow-hard-hover"
          style={{ transition: "transform 150ms ease, box-shadow 150ms ease" }}
          data-testid="upgrade-btn"
        >
          <Sparkles size={14} /> Go Premium
        </button>
      )}
      <button
        data-testid="auth-avatar-btn"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest border-2 border-ink px-2 py-2 bg-paper-surface shadow-hard-sm hover:shadow-hard-hover"
        style={{ transition: "transform 150ms ease, box-shadow 150ms ease" }}
      >
        {user.picture ? (
          <img
            src={user.picture}
            alt={user.name || "user"}
            referrerPolicy="no-referrer"
            className="w-6 h-6 border-2 border-ink object-cover"
          />
        ) : (
          <span className="w-6 h-6 border-2 border-ink bg-press-red text-paper flex items-center justify-center font-mono text-[10px]">
            {initials || "U"}
          </span>
        )}
        <span className="hidden sm:inline max-w-[100px] truncate">{user.name?.split(" ")[0] || "You"}</span>
        {isPremium && <span className="hidden md:inline text-press-red font-bold">✦</span>}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 z-30 bg-paper-surface border-2 border-ink shadow-hard min-w-[220px]"
          data-testid="auth-menu-panel"
        >
          <div className="p-3 border-b-2 border-ink">
            <div className="font-headline font-bold text-sm leading-tight truncate">{user.name}</div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-ink-muted mt-0.5 truncate">{user.email}</div>
            {isPremium ? (
              <div className="mt-2 inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest bg-press-red text-paper px-2 py-0.5">
                <Sparkles size={10} /> Premium member
              </div>
            ) : (
              <div className="mt-2 font-mono text-[9px] uppercase tracking-widest text-ink-muted">
                Free · {entitlement?.plays_remaining ?? "?"}/{entitlement?.plays_limit ?? 2} plays left today
              </div>
            )}
          </div>
          <Link
            to="/favourites"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 px-3 py-2 font-mono text-[11px] uppercase tracking-widest hover:bg-paper-alt"
            data-testid="nav-favourites"
          >
            <Bookmark size={14} /> My Favourites
          </Link>
          {isPremium ? (
            <button
              onClick={openPortal}
              disabled={portalLoading}
              className="w-full text-left flex items-center gap-2 px-3 py-2 font-mono text-[11px] uppercase tracking-widest hover:bg-paper-alt disabled:opacity-60"
              data-testid="stripe-portal-btn"
            >
              {portalLoading ? <Loader2 size={14} className="animate-spin" /> : <Settings size={14} />}
              Manage subscription
            </button>
          ) : (
            <button
              onClick={() => { setOpen(false); setShowPricing(true); trackEvent("upgrade_click", { source: "menu" }); }}
              className="w-full text-left flex items-center gap-2 px-3 py-2 font-mono text-[11px] uppercase tracking-widest text-press-red hover:bg-paper-alt"
              data-testid="upgrade-menu-btn"
            >
              <Sparkles size={14} /> Upgrade to Premium
            </button>
          )}
          <button
            data-testid="logout-btn"
            onClick={async () => { setOpen(false); trackEvent("auth_logout"); await logout(); }}
            className="w-full text-left flex items-center gap-2 px-3 py-2 font-mono text-[11px] uppercase tracking-widest hover:bg-paper-alt border-t border-dashed border-ink"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      )}
      <PricingModal open={showPricing} onClose={() => setShowPricing(false)} reason="manual" playsRemaining={entitlement?.plays_remaining ?? 2} />
    </div>
  );
}
